import { useEffect, useRef, useState } from "react";
import { api, ApiError, type AccessMode, type RoomSummary, type User } from "../lib/api";
import { themeFrom } from "../lib/themes";

type Props = {
  user: User;
  onOpenRoom: (id: string) => void;
  onSignOut: () => void;
};

export function Home({ user, onOpenRoom, onSignOut }: Props) {
  const [rooms, setRooms] = useState<RoomSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState<{ code: string; roomId: string } | null>(null);

  useEffect(() => {
    api
      .listRooms()
      .then((r) => setRooms(r.rooms))
      .catch(() => setRooms([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <span className="dot" />
          rhythm
        </div>
        <div className="row">
          <span className="hint">{user.display_name}</span>
          <button className="btn ghost small" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <div className="home">
        <div className="home-actions">
          <CreateRoom
            onCreated={(room, code) => {
              setRooms((r) => [room, ...r]);
              setNewCode({ code, roomId: room.id });
            }}
            busy={creating}
            setBusy={setCreating}
          />
          <JoinRoom onJoined={onOpenRoom} />
        </div>

        <h2>Your rooms</h2>
        {loading ? (
          <div className="empty">
            <span className="spinner" style={{ margin: "0 auto" }} />
          </div>
        ) : rooms.length === 0 ? (
          <div className="empty">
            No rooms yet. Make one, then send the code to whoever you want in it.
          </div>
        ) : (
          <div className="room-grid">
            {rooms.map((room) => {
              const theme = themeFrom(room.theme);
              return (
                <button
                  key={room.id}
                  className="card room-card"
                  onClick={() => onOpenRoom(room.id)}
                >
                  <span
                    style={{
                      height: 6,
                      borderRadius: 999,
                      background: theme.swatch,
                      display: "block",
                    }}
                  />
                  <span className="name">{room.name}</span>
                  <span className={`badge ${room.access_mode}`}>
                    {room.access_mode === "open" ? "Open" : "Ask to join"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {newCode && (
        <div className="overlay" onClick={() => setNewCode(null)}>
          <div className="card modal" onClick={(e) => e.stopPropagation()}>
            <h3>Room created</h3>
            <p className="hint" style={{ marginTop: 0 }}>
              Share this code with whoever you want in the room. You can make more
              codes, or revoke this one, from inside the room.
            </p>
            <div className="code-display">{newCode.code}</div>
            <p className="hint" style={{ marginTop: 10 }}>
              You can find this again under Invite once you're in the room.
            </p>
            <div className="row" style={{ marginTop: 18 }}>
              <button
                className="btn"
                onClick={() => navigator.clipboard?.writeText(newCode.code)}
              >
                Copy code
              </button>
              <button
                className="btn primary"
                style={{ flex: 1 }}
                onClick={() => onOpenRoom(newCode.roomId)}
              >
                Go to room
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateRoom({
  onCreated,
  busy,
  setBusy,
}: {
  onCreated: (room: RoomSummary, code: string) => void;
  busy: boolean;
  setBusy: (b: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState<AccessMode>("closed");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { room, join_code } = await api.createRoom(name, mode);
      setName("");
      onCreated(room, join_code.code!);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create the room.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h3 style={{ margin: "0 0 14px", fontSize: 17 }}>Start a room</h3>

      <div className="field">
        <label htmlFor="room-name">Name it</label>
        <input
          id="room-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Sunday listening"
          maxLength={80}
          required
        />
      </div>

      <div className="field">
        <label>Who can get in?</label>
        <div className="seg">
          <button
            type="button"
            className={mode === "open" ? "active" : ""}
            onClick={() => setMode("open")}
          >
            Open
          </button>
          <button
            type="button"
            className={mode === "closed" ? "active" : ""}
            onClick={() => setMode("closed")}
          >
            Ask first
          </button>
        </div>
        <p className="hint">
          {mode === "open"
            ? "Anyone with the code walks straight in."
            : "The code gets them a knock. You decide who comes in."}
        </p>
      </div>

      {error && <p className="error">{error}</p>}

      <button className="btn primary" type="submit" disabled={busy || !name.trim()}>
        {busy ? <span className="spinner" /> : "Create room"}
      </button>
    </form>
  );
}

function JoinRoom({ onJoined }: { onJoined: (id: string) => void }) {
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [knockId, setKnockId] = useState<number | null>(null);
  const [denied, setDenied] = useState(false);
  // Held in a ref so a caller passing a fresh arrow each render doesn't keep
  // restarting the poll below before it ever fires.
  const onJoinedRef = useRef(onJoined);
  onJoinedRef.current = onJoined;

  // A knock is decided by someone else, so the answer has to be waited for.
  // Polling the knock is enough: it flips to approved the moment a host lets
  // us in, and carries the room id we were never told up front.
  useEffect(() => {
    if (knockId === null) return;
    let alive = true;

    const timer = setInterval(async () => {
      try {
        const result = await api.getKnock(knockId);
        if (!alive || result.status === "pending") return;
        clearInterval(timer);
        if (result.status === "approved" && result.room_id) {
          setKnockId(null);
          onJoinedRef.current(result.room_id);
        } else {
          setKnockId(null);
          setDenied(true);
        }
      } catch {
        // A transient failure just means we try again on the next tick.
      }
    }, 3000);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [knockId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setDenied(false);
    setKnockId(null);
    try {
      const result = await api.join(code, message || undefined);
      if (result.status === "joined") {
        onJoined(result.room.id);
      } else {
        // A closed room tells us nothing but "your request is in" — from here
        // we wait on the host rather than making them re-enter the code.
        setKnockId(result.knock_id);
        setCode("");
        setMessage("");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't join.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h3 style={{ margin: "0 0 14px", fontSize: 17 }}>Join with a code</h3>

      <div className="field">
        <label htmlFor="join-code">Code</label>
        <input
          id="join-code"
          className="input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="XXXX-XXXX-XXXX"
          style={{ fontFamily: "var(--mono)", letterSpacing: "0.1em" }}
          required
        />
      </div>

      <div className="field">
        <label htmlFor="join-msg">Note (if they have to approve you)</label>
        <input
          id="join-msg"
          className="input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="it's me!"
          maxLength={280}
        />
      </div>

      {error && <p className="error">{error}</p>}
      {knockId !== null && (
        <p className="hint" style={{ color: "var(--accent)" }}>
          <span className="spinner" /> Request sent. Waiting to be let in — you'll go
          straight through.
        </p>
      )}
      {denied && <p className="hint">That request wasn't accepted.</p>}

      <button className="btn" type="submit" disabled={busy || !code.trim()}>
        {busy ? <span className="spinner" /> : "Join"}
      </button>
    </form>
  );
}
